/**
 * The backlinks panel (`070-search-and-links.md` §7.6).
 *
 * **`070` calls this the killer feature, and it means it.** For an NPC or a location page,
 * this panel *is* the page's real content: `Sszelvyn the Grey` may itself be three lines of
 * stat block, but his backlinks panel is the complete, automatically-maintained,
 * chronologically-ordered list of every session he has ever appeared in — assembled with
 * zero curation effort, purely because someone typed `[[Sszelvyn]]` while taking notes at
 * speed. That is the entire value of a wiki-linked notebook over a folder of documents.
 *
 * So it must be prominent, fast, and never wrong:
 *
 *  - **Prominent.** It sits in the DOCUMENT FLOW, below the editor — NOT in a right-hand
 *    drawer. It is part of the page's meaning, not a tool panel about the page.
 *  - **Fast.** One indexed lookup (`idx_page_link_target`), cached for a minute.
 *  - **Never wrong.** The graph is derived SERVER-SIDE from the stored document on every
 *    save (`070` §7.3). The client never sends the link list, and this component never
 *    computes it from the editor's content.
 *
 * The second group is the one people fall in love with: every `[[…]]` you have written that
 * does not exist yet, with a button to make it. The link you typed at speed *becomes the
 * to-do*.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { ChevronRight, FileText, Link2, Plus } from 'lucide-react';
import type { Backlink } from '@/api/endpoints/pages';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { useBacklinks } from '@/features/notebook/usePageMutations';

function breadcrumbOf(link: Backlink): string {
  const titles = link.breadcrumb.map((crumb) => crumb.title);
  return titles.length > 2 ? `… / ${titles.slice(-2).join(' / ')}` : titles.join(' / ');
}

export function BacklinksPanel({
  pageId,
  onCreatePage,
}: {
  pageId: string;
  /** Creates the page as a CHILD of this one — "almost always the right home" (`070` §7.6). */
  onCreatePage: (title: string) => void;
}) {
  const { t } = useTranslation();
  const result = useBacklinks(pageId);

  const backlinks = result.data?.backlinks ?? [];
  const unresolved = result.data?.unresolved ?? [];
  const empty = backlinks.length === 0 && unresolved.length === 0;

  // "Collapsed by default when both groups are empty, expanded otherwise" (`070` §7.6).
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  const open = collapsed === null ? !empty : !collapsed;

  if (result.isPending || result.isError) {
    // A backlinks panel that cannot load is not worth a red box under every page: the page
    // itself is fine, and the panel will be there on the next fetch.
    return null;
  }

  return (
    <section
      data-testid="backlinks"
      className="mx-auto mt-6 max-w-[78ch] border-t border-[var(--border)] px-6 pt-3 pb-8"
      aria-label={t('notebook.backlinks.ariaLabel')}
    >
      <button
        type="button"
        aria-expanded={open}
        data-testid="backlinks-toggle"
        onClick={() => {
          setCollapsed(open);
        }}
        className="flex h-[var(--row-h)] w-full items-center gap-1.5 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
      >
        <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} />
        <Link2 className="size-[var(--icon)]" strokeWidth={1.75} />
        <span className="text-xs font-medium tracking-wide uppercase">
          {t('notebook.backlinks.title')} {backlinks.length > 0 && `(${String(backlinks.length)})`}
        </span>
      </button>

      {open && (
        <div className="mt-1">
          {backlinks.length === 0 ? (
            <p className="px-1 py-1 text-[var(--muted-foreground)]">
              {t('notebook.backlinks.none')}
            </p>
          ) : (
            <ul data-testid="backlinks-list">
              {backlinks.map((link) => (
                <li key={link.sourcePageId}>
                  <Link
                    to={`/p/${link.sourcePageId}`}
                    className="flex h-[var(--row-h)] items-center gap-1.5 rounded-sm px-1 hover:bg-[var(--accent)]"
                  >
                    {link.icon === null ? (
                      <FileText className="size-[var(--icon)] shrink-0" strokeWidth={1.75} />
                    ) : (
                      <span
                        aria-hidden
                        className="size-[var(--icon)] shrink-0 text-center leading-none"
                      >
                        {link.icon}
                      </span>
                    )}
                    <span className="truncate">{link.sourceTitle}</span>
                    {link.breadcrumb.length > 0 && (
                      <span className="ml-auto shrink-0 truncate pl-2 text-2xs text-[var(--muted-foreground)]">
                        {breadcrumbOf(link)}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {unresolved.length > 0 && (
            <>
              <h3 className="mt-3 mb-1 text-xs font-medium tracking-wide text-[var(--muted-foreground)] uppercase">
                {t('notebook.backlinks.unresolvedHeading', { count: unresolved.length })}
              </h3>

              <ul data-testid="unresolved-list">
                {unresolved.map((link) => (
                  <li
                    key={link.targetTitle}
                    className="flex h-[var(--row-h)] items-center gap-1.5 rounded-sm px-1 hover:bg-[var(--accent)]"
                  >
                    {/* Muted and dashed, never red: an unwritten page is an invitation, not
                        an error (`070` §7.2). */}
                    <span className="truncate text-[var(--muted-foreground)] underline decoration-dashed underline-offset-2">
                      {link.targetTitle}
                    </span>

                    <button
                      type="button"
                      data-testid={`create-${link.targetTitle}`}
                      onClick={() => {
                        onCreatePage(link.targetTitle);
                      }}
                      className="ml-auto inline-flex h-5 shrink-0 items-center gap-1 rounded-sm border border-[var(--border)] px-1.5 text-2xs hover:bg-[var(--background)]"
                    >
                      <Plus className="size-3" strokeWidth={2} />
                      {t('common.create')}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
